/**
 * Integrytas Group - Common Security Module (CSM)
 * Contextual AI Assistance — shared TypeScript contracts.
 *
 * These types are the application's NATIVE representation of an AI request and
 * its result. The LLM never sees the app; the csm-ai Edge Function translates
 * natural language into these structures, and the UI applies them directly to
 * grid / form state. No SQL is ever generated or executed.
 *
 * The operator vocabulary here is kept in lockstep with:
 *   - supabase/functions/csm-ai/index.ts (ALLOWED_OPS — server-side validation)
 *   - src/lib/ai/grid_filter.ts          (the client-side evaluator)
 */

export type AiFieldType = 'string' | 'number' | 'boolean' | 'date' | 'enum';

/** A single grid column the AI is allowed to reference. */
export interface AiColumnSchema {
  key:    string;
  label?: string;
  type?:  AiFieldType;
}

/** A single form field the AI may pre-fill during Smart Paste. */
export interface AiFieldSchema {
  key:       string;
  label?:    string;
  type?:     AiFieldType;
  required?: boolean;
}

// -----------------------------------------------------------------------------
// Native filter / sort model (NLQ output)
// -----------------------------------------------------------------------------

export type AiFilterOp =
  | 'eq' | 'neq' | 'contains' | 'starts_with' | 'ends_with'
  | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'between'
  | 'is_empty' | 'is_not_empty' | 'before' | 'after';

export interface AiFilter {
  column:  string;
  op:      AiFilterOp;
  value?:  unknown;
  /** Upper bound — used only by the `between` operator. */
  value2?: unknown;
}

export interface AiSort {
  column: string;
  dir:    'asc' | 'desc';
}

/** All filters combine with logical AND; sorts apply in order. */
export interface AiQuery {
  filters:      AiFilter[];
  sorts:        AiSort[];
  explanation?: string;
}

export interface AiNlqResult extends AiQuery {
  provider: string;
  model:    string;
}

// -----------------------------------------------------------------------------
// Smart Paste / Document Extraction (EXTRACT output)
// -----------------------------------------------------------------------------

export interface AiExtractResult {
  /** Proposed field values, keyed by AiFieldSchema.key. For user review only. */
  values:   Record<string, unknown>;
  notes:    string;
  provider: string;
  model:    string;
}

/** A binary document/image for extraction (base64, no data: prefix). */
export interface AiDocumentInput {
  media_type: string;   // 'application/pdf' | 'image/png' | 'image/jpeg' | ...
  data_base64: string;
  name?: string;
}

// -----------------------------------------------------------------------------
// Per-screen configuration (mirrors csm_ai_resolve_config JSONB)
// -----------------------------------------------------------------------------

export interface AiScreenConfig {
  page_code:         string;
  enabled:           boolean;
  allow_nlq:         boolean;
  allow_smart_paste: boolean;
  redacted_fields:   string[];
  max_context_rows:  number;
}

export const AI_DISABLED_CONFIG: AiScreenConfig = {
  page_code:         '',
  enabled:           false,
  allow_nlq:         false,
  allow_smart_paste: false,
  redacted_fields:   [],
  max_context_rows:  0,
};

// -----------------------------------------------------------------------------
// Serialized workspace state (the "Semantic Screen & Data Mapping" payload)
// -----------------------------------------------------------------------------

export interface WorkspacePermissions {
  can_view:   boolean;
  can_add:    boolean;
  can_change: boolean;
  can_delete: boolean;
}

export interface WorkspaceContext {
  page_code: string;
  screen: {
    internal_code: string;
    route_path?:   string;
    title?:        string;
  };
  grid_schema:     AiColumnSchema[];
  selected_record: Record<string, unknown> | null;
  sample_rows:     Record<string, unknown>[];
  permissions:     WorkspacePermissions;
  user: {
    user_id:          number;
    preferred_locale: string;
    roles:            string[];
  };
}
